import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Stack } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import {
  setUsersData,
  setUsersLoading,
  setUsersLeadToQualifiedData,
  setUsersLeadToQualifiedLoading,
  setUsersQualifiedByZipData,
  setUsersQualifiedByZipLoading,
  setUsersStep1AllData,
  setUsersStep1AllLoading,
  setUsersStep1LeadData,
  setUsersStep1LeadLoading,
  setUsersStep1QualifiedData,
  setUsersStep1QualifiedLoading,
  setUsersStep2AllData,
  setUsersStep2AllLoading,
  setUsersStep2LeadData,
  setUsersStep2LeadLoading,
  setUsersStep2QualifiedData,
  setUsersStep2QualifiedLoading,
  setUsersStep3AllData,
  setUsersStep3AllLoading,
  setUsersStep3LeadData,
  setUsersStep3LeadLoading,
  setUsersStep3QualifiedData,
  setUsersStep3QualifiedLoading,
  setUsersStep4AllData,
  setUsersStep4AllLoading,
  setUsersStep4LeadData,
  setUsersStep4LeadLoading,
  setUsersStep4QualifiedData,
  setUsersStep4QualifiedLoading,
  setUsersStep5LeadData,
  setUsersStep5LeadLoading,
  setUsersStep5QualifiedData,
  setUsersStep5QualifiedLoading,
  setUsersStep6AllData,
  setUsersStep6AllLoading,
  setUsersStep6LeadData,
  setUsersStep6LeadLoading,
  setUsersStep6QualifiedData,
  setUsersStep6QualifiedLoading,
  setUsersStep7AllData,
  setUsersStep7AllLoading,
  setUsersStep7LeadData,
  setUsersStep7LeadLoading,
  setUsersStep7QualifiedData,
  setUsersStep7QualifiedLoading,
} from "../../../../../features/optifundDashboardSlice";
import { OptifundDashboardQueryIds } from "../../../../../pages/AssetManagement/Overview/OptifundDashboard/logic/OptifundDashboardQueryIds";
import { CsvSection } from "../csvExport";
import { useOptifundTabData } from "../hooks/useOptifundTabData";
import { resolveUsersDateRange } from "./usersDateDefaults";
import {
  DonutSegment,
  FunnelStep,
  mapBorrowAmountStepSection,
  mapBusinessBankingStepSection,
  mapBusinessIndustryStepSection,
  mapLeadToQualifiedSegments,
  mapLoanPurposeStepSection,
  mapMainFunnelApiRows,
  mapMonthlyRevenueStepSection,
  mapPersonalCreditStepSection,
  mapQualifiedByZipSegments,
  mapTimeInBusinessStepSection,
  StepBarSection,
  StepDonutSection,
  toBarSectionCsvSection,
  toDonutSectionCsvSection,
  toDonutSegmentsCsvSection,
  toMainFunnelCsvSection,
} from "./usersApiMapping";
import UsersFilterCard from "./UsersFilterCard";
import UsersKpiCards from "./UsersKpiCards";
import UsersFunnel from "./UsersFunnel";
import UsersFunnelDonuts from "./UsersFunnelDonuts";
import UsersSkeleton from "./UsersSkeleton";
import UsersStepSections from "./StepSections/UsersStepSections";

const EMPTY_STEPS: FunnelStep[] = [];
const EMPTY_SEGMENTS: DonutSegment[] = [];

interface UsersProps {
  userToken: any;
  websiteSharedUid: string;
}

export interface UsersExportHandle {
  getExportSections: () => CsvSection[] | null;
}

const Users = forwardRef<UsersExportHandle, UsersProps>(function Users(
  { userToken, websiteSharedUid },
  ref
) {
  const dispatch = useDispatch();
  const {
    usersData,
    usersLoading,
    usersLeadToQualifiedData,
    usersLeadToQualifiedLoading,
    usersQualifiedByZipData,
    usersQualifiedByZipLoading,
    usersStep1AllData,
    usersStep1AllLoading,
    usersStep1LeadData,
    usersStep1LeadLoading,
    usersStep1QualifiedData,
    usersStep1QualifiedLoading,
    usersStep2AllData,
    usersStep2AllLoading,
    usersStep2LeadData,
    usersStep2LeadLoading,
    usersStep2QualifiedData,
    usersStep2QualifiedLoading,
    usersStep3AllData,
    usersStep3AllLoading,
    usersStep3LeadData,
    usersStep3LeadLoading,
    usersStep3QualifiedData,
    usersStep3QualifiedLoading,
    usersStep4AllData,
    usersStep4AllLoading,
    usersStep4LeadData,
    usersStep4LeadLoading,
    usersStep4QualifiedData,
    usersStep4QualifiedLoading,
    usersStep5LeadData,
    usersStep5LeadLoading,
    usersStep5QualifiedData,
    usersStep5QualifiedLoading,
    usersStep6AllData,
    usersStep6AllLoading,
    usersStep6LeadData,
    usersStep6LeadLoading,
    usersStep6QualifiedData,
    usersStep6QualifiedLoading,
    usersStep7AllData,
    usersStep7AllLoading,
    usersStep7LeadData,
    usersStep7LeadLoading,
    usersStep7QualifiedData,
    usersStep7QualifiedLoading,
  } = useSelector((state: any) => state.optifundDashboard);

  // Date range for this tab lives here (not Redux) and is synced with the
  // URL directly via useSearchParams - see docs/new-module-checklist.md
  // Section 5/8.12.
  const [searchParams, setSearchParams] = useSearchParams();
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>(() => {
    const start = searchParams.get("users_start");
    const end = searchParams.get("users_end");
    return start && end && dayjs(start).isValid() && dayjs(end).isValid()
      ? [dayjs(start), dayjs(end)]
      : [null, null];
  });

  useEffect(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        const [start, end] = dateRange;
        if (start && end) {
          next.set("users_start", start.format("YYYY-MM-DD"));
          next.set("users_end", end.format("YYYY-MM-DD"));
        } else {
          next.delete("users_start");
          next.delete("users_end");
        }
        return next;
      },
      { replace: true }
    );
  }, [dateRange, setSearchParams]);

  // start_date/end_date are required on every Users query (spec Section 2)
  // - resolveUsersDateRange falls back to the last-30-days default whenever
  // the user hasn't picked dates yet, so a request is never sent without
  // them. Every query below shares this same tab-level date range.
  const [startDate, endDate] = resolveUsersDateRange(dateRange);
  const startDateParam = startDate.format("YYYY-MM-DD");
  const endDateParam = endDate.format("YYYY-MM-DD");

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersMainFunnelId,
    (data) => dispatch(setUsersData(data)),
    (loading) => dispatch(setUsersLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersLeadToQualifiedId,
    (data) => dispatch(setUsersLeadToQualifiedData(data)),
    (loading) => dispatch(setUsersLeadToQualifiedLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersQualifiedByZipId,
    (data) => dispatch(setUsersQualifiedByZipData(data)),
    (loading) => dispatch(setUsersQualifiedByZipLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep1AllId,
    (data) => dispatch(setUsersStep1AllData(data)),
    (loading) => dispatch(setUsersStep1AllLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep1LeadId,
    (data) => dispatch(setUsersStep1LeadData(data)),
    (loading) => dispatch(setUsersStep1LeadLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep1QualifiedId,
    (data) => dispatch(setUsersStep1QualifiedData(data)),
    (loading) => dispatch(setUsersStep1QualifiedLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep2AllId,
    (data) => dispatch(setUsersStep2AllData(data)),
    (loading) => dispatch(setUsersStep2AllLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep2LeadId,
    (data) => dispatch(setUsersStep2LeadData(data)),
    (loading) => dispatch(setUsersStep2LeadLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep2QualifiedId,
    (data) => dispatch(setUsersStep2QualifiedData(data)),
    (loading) => dispatch(setUsersStep2QualifiedLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep3AllId,
    (data) => dispatch(setUsersStep3AllData(data)),
    (loading) => dispatch(setUsersStep3AllLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep3LeadId,
    (data) => dispatch(setUsersStep3LeadData(data)),
    (loading) => dispatch(setUsersStep3LeadLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep3QualifiedId,
    (data) => dispatch(setUsersStep3QualifiedData(data)),
    (loading) => dispatch(setUsersStep3QualifiedLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep4AllId,
    (data) => dispatch(setUsersStep4AllData(data)),
    (loading) => dispatch(setUsersStep4AllLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep4LeadId,
    (data) => dispatch(setUsersStep4LeadData(data)),
    (loading) => dispatch(setUsersStep4LeadLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep4QualifiedId,
    (data) => dispatch(setUsersStep4QualifiedData(data)),
    (loading) => dispatch(setUsersStep4QualifiedLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep5LeadId,
    (data) => dispatch(setUsersStep5LeadData(data)),
    (loading) => dispatch(setUsersStep5LeadLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep5QualifiedId,
    (data) => dispatch(setUsersStep5QualifiedData(data)),
    (loading) => dispatch(setUsersStep5QualifiedLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep6AllId,
    (data) => dispatch(setUsersStep6AllData(data)),
    (loading) => dispatch(setUsersStep6AllLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep6LeadId,
    (data) => dispatch(setUsersStep6LeadData(data)),
    (loading) => dispatch(setUsersStep6LeadLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep6QualifiedId,
    (data) => dispatch(setUsersStep6QualifiedData(data)),
    (loading) => dispatch(setUsersStep6QualifiedLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep7AllId,
    (data) => dispatch(setUsersStep7AllData(data)),
    (loading) => dispatch(setUsersStep7AllLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep7LeadId,
    (data) => dispatch(setUsersStep7LeadData(data)),
    (loading) => dispatch(setUsersStep7LeadLoading(loading)),
    startDateParam,
    endDateParam
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.usersStep7QualifiedId,
    (data) => dispatch(setUsersStep7QualifiedData(data)),
    (loading) => dispatch(setUsersStep7QualifiedLoading(loading)),
    startDateParam,
    endDateParam
  );

  const funnelSteps: FunnelStep[] = useMemo(
    () => (Array.isArray(usersData) ? mapMainFunnelApiRows(usersData) : EMPTY_STEPS),
    [usersData]
  );

  const leadToQualifiedSegments: DonutSegment[] = useMemo(
    () =>
      Array.isArray(usersLeadToQualifiedData)
        ? mapLeadToQualifiedSegments(usersLeadToQualifiedData)
        : EMPTY_SEGMENTS,
    [usersLeadToQualifiedData]
  );

  const qualifiedByZipSegments: DonutSegment[] = useMemo(
    () =>
      Array.isArray(usersQualifiedByZipData)
        ? mapQualifiedByZipSegments(usersQualifiedByZipData)
        : EMPTY_SEGMENTS,
    [usersQualifiedByZipData]
  );

  const areStep1QueriesReady =
    Array.isArray(usersStep1AllData) &&
    Array.isArray(usersStep1LeadData) &&
    Array.isArray(usersStep1QualifiedData);

  const step1Section: StepBarSection | null = useMemo(
    () =>
      areStep1QueriesReady
        ? mapBorrowAmountStepSection(usersStep1AllData, usersStep1LeadData, usersStep1QualifiedData)
        : null,
    [areStep1QueriesReady, usersStep1AllData, usersStep1LeadData, usersStep1QualifiedData]
  );

  const areStep2QueriesReady =
    Array.isArray(usersStep2AllData) &&
    Array.isArray(usersStep2LeadData) &&
    Array.isArray(usersStep2QualifiedData);

  const step2Section: StepBarSection | null = useMemo(
    () =>
      areStep2QueriesReady
        ? mapLoanPurposeStepSection(usersStep2AllData, usersStep2LeadData, usersStep2QualifiedData)
        : null,
    [areStep2QueriesReady, usersStep2AllData, usersStep2LeadData, usersStep2QualifiedData]
  );

  const areStep3QueriesReady =
    Array.isArray(usersStep3AllData) &&
    Array.isArray(usersStep3LeadData) &&
    Array.isArray(usersStep3QualifiedData);

  const step3Section: StepBarSection | null = useMemo(
    () =>
      areStep3QueriesReady
        ? mapTimeInBusinessStepSection(usersStep3AllData, usersStep3LeadData, usersStep3QualifiedData)
        : null,
    [areStep3QueriesReady, usersStep3AllData, usersStep3LeadData, usersStep3QualifiedData]
  );

  const areStep4QueriesReady =
    Array.isArray(usersStep4AllData) &&
    Array.isArray(usersStep4LeadData) &&
    Array.isArray(usersStep4QualifiedData);

  const step4Section: StepBarSection | null = useMemo(
    () =>
      areStep4QueriesReady
        ? mapMonthlyRevenueStepSection(usersStep4AllData, usersStep4LeadData, usersStep4QualifiedData)
        : null,
    [areStep4QueriesReady, usersStep4AllData, usersStep4LeadData, usersStep4QualifiedData]
  );

  const areStep5QueriesReady =
    Array.isArray(usersStep5LeadData) && Array.isArray(usersStep5QualifiedData);

  const step5Section: StepDonutSection | null = useMemo(
    () =>
      areStep5QueriesReady
        ? mapBusinessBankingStepSection(usersStep5LeadData, usersStep5QualifiedData)
        : null,
    [areStep5QueriesReady, usersStep5LeadData, usersStep5QualifiedData]
  );

  const areStep6QueriesReady =
    Array.isArray(usersStep6AllData) &&
    Array.isArray(usersStep6LeadData) &&
    Array.isArray(usersStep6QualifiedData);

  const step6Section: StepBarSection | null = useMemo(
    () =>
      areStep6QueriesReady
        ? mapBusinessIndustryStepSection(usersStep6AllData, usersStep6LeadData, usersStep6QualifiedData)
        : null,
    [areStep6QueriesReady, usersStep6AllData, usersStep6LeadData, usersStep6QualifiedData]
  );

  const areStep7QueriesReady =
    Array.isArray(usersStep7AllData) &&
    Array.isArray(usersStep7LeadData) &&
    Array.isArray(usersStep7QualifiedData);

  const step7Section: StepBarSection | null = useMemo(
    () =>
      areStep7QueriesReady
        ? mapPersonalCreditStepSection(usersStep7AllData, usersStep7LeadData, usersStep7QualifiedData)
        : null,
    [areStep7QueriesReady, usersStep7AllData, usersStep7LeadData, usersStep7QualifiedData]
  );

  const total = funnelSteps.find((step) => step.label === "click_in")?.count ?? 0;
  const leads = funnelSteps.find((step) => step.label === "Contact details")?.count ?? 0;
  const qualified = funnelSteps.find((step) => step.label === "Qualified")?.count ?? 0;

  const leadToQualifiedTotal = leadToQualifiedSegments.reduce((sum, segment) => sum + segment.value, 0);
  const qualifiedByZipTotal = qualifiedByZipSegments.reduce((sum, segment) => sum + segment.value, 0);

  const isLoading =
    usersLoading ||
    usersLeadToQualifiedLoading ||
    usersQualifiedByZipLoading ||
    usersStep1AllLoading ||
    usersStep1LeadLoading ||
    usersStep1QualifiedLoading ||
    usersStep2AllLoading ||
    usersStep2LeadLoading ||
    usersStep2QualifiedLoading ||
    usersStep3AllLoading ||
    usersStep3LeadLoading ||
    usersStep3QualifiedLoading ||
    usersStep4AllLoading ||
    usersStep4LeadLoading ||
    usersStep4QualifiedLoading ||
    usersStep5LeadLoading ||
    usersStep5QualifiedLoading ||
    usersStep6AllLoading ||
    usersStep6LeadLoading ||
    usersStep6QualifiedLoading ||
    usersStep7AllLoading ||
    usersStep7LeadLoading ||
    usersStep7QualifiedLoading ||
    !Array.isArray(usersData) ||
    !Array.isArray(usersLeadToQualifiedData) ||
    !Array.isArray(usersQualifiedByZipData) ||
    !step1Section ||
    !step2Section ||
    !step3Section ||
    !step4Section ||
    !step5Section ||
    !step6Section ||
    !step7Section;

  const isReadyToExport =
    !isLoading &&
    step1Section &&
    step2Section &&
    step3Section &&
    step4Section &&
    step5Section &&
    step6Section &&
    step7Section;

  useImperativeHandle(
    ref,
    () => ({
      getExportSections: (): CsvSection[] | null => {
        if (!isReadyToExport) return null;
        return [
          toMainFunnelCsvSection(funnelSteps, total),
          toDonutSegmentsCsvSection("Lead to qualified", leadToQualifiedSegments),
          toDonutSegmentsCsvSection("Qualified by zip code", qualifiedByZipSegments),
          toBarSectionCsvSection(step1Section),
          toBarSectionCsvSection(step2Section),
          toBarSectionCsvSection(step3Section),
          toBarSectionCsvSection(step4Section),
          toDonutSectionCsvSection(step5Section),
          toBarSectionCsvSection(step6Section),
          toBarSectionCsvSection(step7Section),
        ];
      },
    }),
    [
      isReadyToExport,
      funnelSteps,
      total,
      leadToQualifiedSegments,
      qualifiedByZipSegments,
      step1Section,
      step2Section,
      step3Section,
      step4Section,
      step5Section,
      step6Section,
      step7Section,
    ]
  );

  if (
    isLoading ||
    !step1Section ||
    !step2Section ||
    !step3Section ||
    !step4Section ||
    !step5Section ||
    !step6Section ||
    !step7Section
  ) {
    return <UsersSkeleton />;
  }

  return (
    <Stack spacing={2.5}>
      <UsersFilterCard dateRange={dateRange} onDateRangeChange={setDateRange} />
      <UsersKpiCards total={total} leads={leads} qualified={qualified} />
      <UsersFunnel steps={funnelSteps} total={total} />
      <UsersFunnelDonuts
        leadToQualifiedSegments={leadToQualifiedSegments}
        qualifiedByZipSegments={qualifiedByZipSegments}
        leads={leadToQualifiedTotal}
        qualified={qualifiedByZipTotal}
      />
      <UsersStepSections
        step1Section={step1Section}
        step2Section={step2Section}
        step3Section={step3Section}
        step4Section={step4Section}
        step5Section={step5Section}
        step6Section={step6Section}
        step7Section={step7Section}
      />
    </Stack>
  );
});

export default Users;
